import { useCallback } from 'react';

/**
 * Hook to manage assessment step navigation and validation.
 *
 * Responsibilities:
 * - Implements `goToNextStep` and `goToPreviousStep` based on current step
 * - Performs all validations required before progressing
 *
 * NOTE: This hook is intentionally focused on logic only (no JSX).
 */
export const useAssessmentSteps = ({
  step,
  setStep,
  facilityInfo,
  energyBaseline,
  questions,
  answers,
  currentQuestionIndex,
  setCurrentQuestionIndex,
  setError
}) => {

  const goToNextStep = useCallback(() => {
    if (step === 1) {
      if (
        !facilityInfo.companyName ||
        !facilityInfo.contactName ||
        !facilityInfo.email ||
        !facilityInfo.majorIndustry ||
        !facilityInfo.state
      ) {
        setError?.(
          'Please fill in all required fields including major industry selection and state/province'
        );
        return;
      }
      setError?.(null);
      setStep(2);
    } else if (step === 2) {
      if (!facilityInfo.subIndustry) {
        setError?.('Please select a sub-industry');
        return;
      }
      setError?.(null);
      setStep(2.5);
    } else if (step === 2.5) {
      if (
        !energyBaseline.annualKwh ||
        !energyBaseline.annualElectricityCost ||
        !energyBaseline.averageMonthlyBill
      ) {
        setError?.(
          'Please provide at least annual kWh usage, annual electricity cost, and average monthly bill'
        );
        return;
      }
      if (!energyBaseline.annualProduction || !energyBaseline.productionUnit) {
        setError?.(
          'Please provide annual production volume and production unit for accurate productivity analysis'
        );
        return;
      }
      setError?.(null);
      setStep(3);
    } else if (step === 3 && currentQuestionIndex < questions.length - 1) {
      // Check if current question is required and answered before moving to next
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion?.required && !answers[currentQuestion.id]) {
        setError?.('Please answer this required question before proceeding');
        return;
      }
      setError?.(null);
      setCurrentQuestionIndex?.((prev) => prev + 1);
    } else if (step === 3 && currentQuestionIndex === questions.length - 1) {
      // Validate all required questions are answered before moving to summary
      const unansweredRequired = questions.filter(
        (q) => q.required && !answers[q.id]
      );
      if (unansweredRequired.length > 0) {
        setError?.(
          `Please answer all required questions. ${unansweredRequired.length} required question(s) remaining.`
        );
        return;
      }
      setError?.(null);
      setStep(4);
    }
  }, [
    step,
    facilityInfo.companyName,
    facilityInfo.contactName,
    facilityInfo.email,
    facilityInfo.majorIndustry,
    facilityInfo.state,
    facilityInfo.subIndustry,
    energyBaseline.annualKwh,
    energyBaseline.annualElectricityCost,
    energyBaseline.averageMonthlyBill,
    energyBaseline.annualProduction,
    energyBaseline.productionUnit,
    currentQuestionIndex,
    questions,
    answers,
    setError,
    setCurrentQuestionIndex
  ]);

  const goToPreviousStep = useCallback(() => {
    if (step === 4) {
      setStep(3);
    } else if (step === 3 && currentQuestionIndex > 0) {
      setCurrentQuestionIndex?.((prev) => prev - 1);
    } else if (step === 3 && currentQuestionIndex === 0) {
      setStep(2.5);
    } else if (step === 2.5) {
      setStep(2);
    } else if (step === 2) {
      setStep(1);
    }
  }, [step, currentQuestionIndex, setCurrentQuestionIndex]);

  return {
    step,
    goToNextStep,
    goToPreviousStep
  };
};


